import ProductType from "../../../enum/ProductType";

type PieceCountLine = {
  type?: string;
  pieces?: number | string | null;
  quantity?: number | string | null;
  qty?: number | string | null;
  pieceCount?: number | null;
  __pieceCountTouched?: boolean;
};

const getNumericQuantity = (line: PieceCountLine): number => {
  const value = Number(line.quantity ?? line.qty ?? 0);
  return Number.isFinite(value) ? value : 0;
};

export const parseOptionalPieceCount = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return { ok: true as const, value: null };
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0 || !Number.isInteger(numericValue)) {
    return { ok: false as const, value: null };
  }

  return { ok: true as const, value: numericValue };
};

const normalizePieceCount = (value: unknown): number | null => {
  const parsedValue = parseOptionalPieceCount(value);
  return parsedValue.ok ? parsedValue.value : null;
};

const deriveNormalLinePieceCount = (line: PieceCountLine): number | null => {
  const quantity = getNumericQuantity(line);
  if (quantity === 0) return 0;

  const piecesPerUnit = Number(line.pieces ?? 0);
  if (!Number.isFinite(piecesPerUnit)) return null;

  const derivedPieceCount = piecesPerUnit * quantity;
  return Number.isFinite(derivedPieceCount) && Number.isInteger(derivedPieceCount)
    ? derivedPieceCount
    : null;
};

const isMeasuredLine = (line: PieceCountLine): boolean => {
  return line.type === ProductType.WEIGHT || line.type === ProductType.LENGTH;
};

export function initializePieceCountForNewLine<T extends PieceCountLine>(line: T): T {
  return {
    ...line,
    pieceCount: isMeasuredLine(line) ? null : deriveNormalLinePieceCount(line),
    __pieceCountTouched: false
  };
}

export function hydratePieceCountForExistingLine<T extends PieceCountLine>(line: T): T {
  const explicitPieceCount = normalizePieceCount(line.pieceCount);
  const measuredLine = isMeasuredLine(line);
  const pieceCount = explicitPieceCount ?? (measuredLine ? null : deriveNormalLinePieceCount(line));

  return {
    ...line,
    pieceCount,
    __pieceCountTouched: measuredLine && pieceCount !== null
  };
}

export function maybeRecomputePieceCount<T extends PieceCountLine>(line: T): T {
  const measuredLine = isMeasuredLine(line);
  const touched = Boolean(line.__pieceCountTouched);
  const quantity = getNumericQuantity(line);

  if (measuredLine) {
    return {
      ...line,
      pieceCount: touched ? normalizePieceCount(line.pieceCount) : null,
      __pieceCountTouched: touched
    };
  }

  if (quantity === 0) {
    return {
      ...line,
      pieceCount: 0,
      __pieceCountTouched: false
    };
  }

  return {
    ...line,
    pieceCount: deriveNormalLinePieceCount(line),
    __pieceCountTouched: false
  };
}
